import type { HttpTypes } from "@medusajs/types";

import type {
  StoreOrderHistoryListParams,
  StoreOrderHistoryListResponse,
  StoreOrderHistoryOrder
} from "@repo/api-sdk";
import sdk from "@repo/api-sdk";
import { queryOptions, useQuery } from "@tanstack/react-query";

import {
  deriveOrderStats,
  deriveTopSupplier,
  type CustomerOrderStats,
  type TopSupplier
} from "./order-insights";
import { orderQueryKeys as queryKeys } from "./queryKeys";

// The stats/top-supplier shapes are owned by the order-insights module (which
// also owns the derivation); re-export so consumers keep importing them from
// `@/queries/orders`.
export type { CustomerOrderStats, TopSupplier } from "./order-insights";

// ---- Order confirmation (post-checkout) ----

/**
 * Order shape returned by the confirmation query. Medusa's `StoreOrder` type
 * doesn't include our custom `custom_display_id` field (set via the ORDER
 * module's `generateCustomDisplayId` in medusa-config.ts), so we extend it here.
 */
export type OrderConfirmation = HttpTypes.StoreOrder & {
  custom_display_id?: string | null;
};

const ORDER_CONFIRMATION_FIELDS = [
  "id",
  "display_id",
  "custom_display_id",
  "status",
  "email",
  "currency_code",
  "created_at",
  "item_subtotal",
  "shipping_total",
  "tax_total",
  "discount_total",
  "total",
  "*items",
  "*items.variant",
  "*shipping_address",
  "*shipping_methods"
].join(",");

export function orderConfirmationQuery(orderIds: string[]) {
  return queryOptions({
    queryKey: [...queryKeys.all, "confirmation", ...orderIds] as const,
    queryFn: async () => {
      const res = await sdk.store.order.list({
        id: orderIds,
        fields: ORDER_CONFIRMATION_FIELDS,
        limit: orderIds.length
      });
      return res as Omit<typeof res, "orders"> & {
        orders: OrderConfirmation[];
      };
    },
    retry: false,
    staleTime: 10 * 60 * 1000,
    enabled: orderIds.length > 0
  });
}

export function useOrderConfirmation(orderIds: string[]) {
  return useQuery(orderConfirmationQuery(orderIds));
}

// ---- Order history (F1) ----

/** An order-history row/detail: a store order plus supplier + computed statuses. */
export type CustomerOrder = StoreOrderHistoryOrder;
export type CustomerOrdersResponse = StoreOrderHistoryListResponse;

/**
 * Fields for the order-history LIST rows. Served by the dedicated
 * `/store/order-history` endpoint (salonstocker/api#100), which exposes the
 * supplier (`seller`, via the seller-order link) plus the server-computed
 * `fulfillment_status` + `payment_status`.
 */
const CUSTOMER_ORDER_LIST_FIELDS = [
  "id",
  "display_id",
  "custom_display_id",
  "status",
  "fulfillment_status",
  "payment_status",
  "created_at",
  "currency_code",
  "total",
  "summary.paid_total",
  "summary.original_order_total",
  "seller.id",
  "seller.name",
  "items.id",
  "items.quantity"
].join(",");

/** Richer field set for the single-order detail view. */
const CUSTOMER_ORDER_DETAIL_FIELDS = [
  "id",
  "display_id",
  "custom_display_id",
  "status",
  "fulfillment_status",
  "payment_status",
  "email",
  "currency_code",
  "created_at",
  "item_subtotal",
  "shipping_total",
  "tax_total",
  "discount_total",
  "total",
  "summary.paid_total",
  "summary.original_order_total",
  "seller.id",
  "seller.name",
  "seller.handle",
  "*items",
  "*items.variant",
  "*shipping_address",
  "*shipping_methods"
].join(",");

export interface CustomerOrdersQueryParams {
  limit?: number;
  offset?: number;
  /** Core Medusa order status filter (pending/completed/canceled/...). */
  status?: HttpTypes.StoreOrderFilters["status"];
  /** Supplier filter - the seller id (resolved server-side via the link). */
  seller_id?: string;
  /**
   * Computed fulfillment-status filter - one or more canonical states (e.g.
   * `["shipped", "partially_shipped"]` for the "In transit" tab). Resolved
   * server-side; composes with the other filters and keeps `count` accurate.
   */
  fulfillmentStatus?: string[];
  /** Inclusive created-at range (ISO strings). */
  createdAtFrom?: string;
  createdAtTo?: string;
}

/**
 * Paginated list of the logged-in customer's orders, newest first. One order
 * per seller (no OrderSet grouping - OrderSet is deprecated). Served by
 * `/store/order-history`, which scopes results to the authenticated customer.
 */
export function customerOrdersQuery(params: CustomerOrdersQueryParams = {}) {
  const {
    limit = 10,
    offset = 0,
    status,
    seller_id,
    fulfillmentStatus,
    createdAtFrom,
    createdAtTo
  } = params;

  const created_at =
    createdAtFrom || createdAtTo
      ? {
          ...(createdAtFrom ? { $gte: createdAtFrom } : {}),
          ...(createdAtTo ? { $lte: createdAtTo } : {})
        }
      : undefined;

  const query: StoreOrderHistoryListParams = {
    limit,
    offset,
    order: "-created_at",
    fields: CUSTOMER_ORDER_LIST_FIELDS,
    ...(status ? { status } : {}),
    ...(seller_id ? { seller_id } : {}),
    ...(fulfillmentStatus?.length
      ? { fulfillment_status: fulfillmentStatus }
      : {}),
    ...(created_at ? { created_at } : {})
  };

  return queryOptions({
    queryKey: [...queryKeys.all, "list", query] as const,
    queryFn: async () => {
      return await sdk.store.orderHistory.list(query);
    },
    retry: false,
    staleTime: 60 * 1000
  });
}

export function useCustomerOrders(params?: CustomerOrdersQueryParams) {
  return useQuery(customerOrdersQuery(params));
}

/** Single order for the history detail view (list-by-id on the same endpoint). */
export function customerOrderQuery(orderId: string) {
  return queryOptions({
    queryKey: [...queryKeys.all, "detail", orderId] as const,
    queryFn: async () => {
      const res = await sdk.store.orderHistory.list({
        id: [orderId],
        fields: CUSTOMER_ORDER_DETAIL_FIELDS,
        limit: 1
      });
      return res.orders[0] ?? null;
    },
    retry: false,
    staleTime: 60 * 1000,
    enabled: !!orderId
  });
}

export function useCustomerOrder(orderId: string) {
  return useQuery(customerOrderQuery(orderId));
}

/** A supplier the customer has ordered from - for the order-history filter. */
export interface OrderSupplier {
  id: string;
  name: string;
}

/**
 * Distinct suppliers across the customer's recent orders, for the supplier
 * filter dropdown. Derived client-side from a lean seller-only fetch (salons
 * order from a handful of suppliers, so a single page covers it).
 */
export function customerOrderSuppliersQuery() {
  return queryOptions({
    queryKey: [...queryKeys.all, "suppliers"] as const,
    queryFn: async (): Promise<OrderSupplier[]> => {
      const res = await sdk.store.orderHistory.list({
        fields: "seller.id,seller.name",
        order: "-created_at",
        limit: 200
      });
      const byId = new Map<string, string>();
      for (const order of res.orders) {
        if (order.seller?.id) {
          byId.set(order.seller.id, order.seller.name);
        }
      }
      return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    },
    retry: false,
    staleTime: 5 * 60 * 1000
  });
}

export function useCustomerOrderSuppliers() {
  return useQuery(customerOrderSuppliersQuery());
}

/** How many recent orders to sample when deriving the header stats. */
const STATS_SAMPLE_LIMIT = 200;

/**
 * Header stats for the order-history page (active / completed counts + total
 * spend). Mirrors {@link customerOrderSuppliersQuery}: a single bounded fetch of
 * the lean status/total fields, aggregated client-side (salons place a bounded
 * number of orders). `total` comes from the endpoint's exact `count`; the other
 * figures are derived over the sampled window and flagged `approximate` when the
 * customer has more orders than the sample.
 */
export function customerOrderStatsQuery() {
  return queryOptions({
    queryKey: [...queryKeys.all, "stats"] as const,
    queryFn: async (): Promise<CustomerOrderStats> => {
      const res = await sdk.store.orderHistory.list({
        fields:
          "status,fulfillment_status,total,discount_total,created_at,currency_code",
        order: "-created_at",
        limit: STATS_SAMPLE_LIMIT
      });
      return deriveOrderStats(res.orders, res.count ?? res.orders.length);
    },
    retry: false,
    staleTime: 60 * 1000
  });
}

/**
 * `enabled: false` makes this observer read the shared cache without starting a
 * fetch (the header chip does this below `xl`, where it is not shown). Another
 * observer of the same key with the default `enabled` - the dashboard tile -
 * still fetches.
 */
export function useCustomerOrderStats({
  enabled = true
}: { enabled?: boolean } = {}) {
  return useQuery({ ...customerOrderStatsQuery(), enabled });
}

// ---- Top supplier (home Highlights, storefront#75) ----

/**
 * The customer's most-ordered supplier, derived client-side from a single
 * bounded, lean fetch of recent orders (mirrors {@link customerOrderStatsQuery}
 * and {@link customerOrderSuppliersQuery}). The ranking + cancelled-exclusion
 * live in {@link deriveTopSupplier}; here we just fetch and hand it the window.
 */
export function customerTopSupplierQuery() {
  return queryOptions({
    queryKey: [...queryKeys.all, "top-supplier"] as const,
    queryFn: async (): Promise<TopSupplier | null> => {
      const res = await sdk.store.orderHistory.list({
        fields: "seller.id,seller.name,created_at,status,fulfillment_status",
        order: "-created_at",
        limit: STATS_SAMPLE_LIMIT
      });
      return deriveTopSupplier(res.orders, res.count ?? res.orders.length);
    },
    retry: false,
    staleTime: 60 * 1000
  });
}

export function useCustomerTopSupplier() {
  return useQuery(customerTopSupplierQuery());
}
